/**
 * Grouping trips by the pair of locations they connect.
 *
 * Trips between the same two locations render as identical, perfectly
 * overlapping geometry, so the map can only ever surface one of them under the
 * cursor. Counting them lets the trip layer widen busy routes and lets the
 * tooltip say how many trips a line really represents.
 *
 * These are pure helpers deliberately kept free of any store import, so they
 * stay unit-testable (importing the selectors module pulls in the whole store).
 */

/** Coordinate rounding used when a trip has no location id to key on. ~1m. */
const ENDPOINT_PRECISION = 5;

/** Rendered input trip lines — the features `pairCount` is computed across. */
export const isInputTripLine = feature =>
  feature?.geometry?.type === 'LineString' &&
  feature?.properties?.type === 'trip' &&
  !feature?.properties?.isDrop &&
  !feature?.properties?.isResult;

/**
 * Identify one end of a trip. Prefers the location id, but falls back to
 * rounded coordinates: legacy trip rows carry no fromLocationId/toLocationId
 * (createInitGeoJson resolves them from denormalized lat/lng), and keying
 * those on the undefined ids alone would collapse every legacy trip in the
 * scenario into one bogus bundle.
 */
const endpointKey = (uuid, coord) => {
  if (uuid) return `id:${uuid}`;
  if (!Array.isArray(coord) || coord.length < 2) return 'unknown';
  return `at:${Number(coord[0]).toFixed(ENDPOINT_PRECISION)},${Number(coord[1]).toFixed(ENDPOINT_PRECISION)}`;
};

/**
 * Canonical key for the pair of locations a trip connects. The two endpoints
 * are ordered, so the key is *undirected*: Chicago→Boston and Boston→Chicago
 * produce the same key and count as one route, as do repeated trips along the
 * same direction.
 */
export const tripPairKey = feature => {
  const { fromUuid, toUuid } = feature?.properties || {};
  const coords = feature?.geometry?.coordinates;
  const from = endpointKey(fromUuid, coords?.[0]);
  const to = endpointKey(toUuid, coords?.[coords.length - 1]);
  // Plain codepoint ordering, not localeCompare — this only needs to be a
  // stable canonical order so both directions collide, and locale-dependent
  // collation would make the key vary by environment.
  const [a, b] = from <= to ? [from, to] : [to, from];
  return `${a}||${b}`;
};

/**
 * Stamp each input trip with `pairKey` and `pairCount` (how many trips connect
 * the same two locations, in either direction). Features stay 1:1 with trip
 * rows, so every uuid-keyed interaction — click-to-edit, hover highlight, the
 * directional from/to glow — keeps working; the count is only used to style
 * and describe the line.
 *
 * Returns the input unchanged when nothing needs stamping, so callers can rely
 * on reference equality to skip work.
 */
export const annotateTripPairs = data => {
  const features = data?.features;
  if (!Array.isArray(features) || features.length === 0) return data;

  const keys = features.map(f => (isInputTripLine(f) ? tripPairKey(f) : null));
  const counts = new Map();
  for (const key of keys) {
    if (key) counts.set(key, (counts.get(key) || 0) + 1);
  }
  if (counts.size === 0) return data;

  let changed = false;
  const next = features.map((feature, i) => {
    const key = keys[i];
    if (!key) return feature;
    const pairCount = counts.get(key);
    const props = feature.properties;
    if (props.pairKey === key && props.pairCount === pairCount) return feature;
    changed = true;
    return { ...feature, properties: { ...props, pairKey: key, pairCount } };
  });

  return changed ? { ...data, features: next } : data;
};
